import { useCallback, useEffect, useState } from "react";
import { createContainer, useContainer } from "unstated-next";

import { fetchFavoriteMovies } from "../api/movie-favorite";
import { FetchedMovie, MovieItem } from "../constants/interfaces";
import { useFetch } from "../hooks/http-hook";
import Tokens from "./tokens";
import { getMovieItem } from "../utils/movie";

const useFavorite = () => {
  const [favoriteMovies, setFavoriteMovies] = useState<MovieItem[] | null>(null);
  const [favoriteId, setFavoriteId] = useState<number[] | null>(null);
  const [totalFavorites, setTotalFavorites] = useState<number>(0);

  const { accountId, sessionId } = useContainer(Tokens);
  const { sendRequest, isLoading } = useFetch();

  const processMovieResponse = (response: any) => {
    const fetchedFavorites: FetchedMovie[] = response.results;
    return fetchedFavorites.map((movie) => {
      return { ...getMovieItem(movie), isFavorite: true };
    });
  };

  const fetchFavorites = useCallback(() => {
    if (accountId && sessionId) {
      fetchFavoriteMovies(sendRequest, accountId, sessionId, 1).then((response: any) => {
        const result: FetchedMovie[] = response.results;
        const resultedId = result.map((res) => res.id);
        const totalPages = response.total_pages;

        setFavoriteMovies(processMovieResponse(response));
        setFavoriteId(resultedId);
        setTotalFavorites(response.total_results);

        if (totalPages > 1) {
          for (let page = 2; page <= totalPages; page += 1) {
            fetchFavoriteMovies(sendRequest, accountId, sessionId, page).then((response: any) => {
              let result: FetchedMovie[] = response.results;
              const resultedId = result.map((res) => res.id);
              setFavoriteId((prevFavorite) => prevFavorite!.concat(resultedId));
              setFavoriteMovies((prevState) => prevState!.concat(processMovieResponse(response)));
            });
          }
        }
      });
    }
  }, [accountId, sessionId, sendRequest]);

  const updateFavoriteMovies = (movie: MovieItem) => {
    let updatedFavoriteMovies;

    //the movie is already in favorite, so the user wants to remove it
    if (movie.isFavorite) {
      updatedFavoriteMovies = favoriteMovies!.filter((favMovie) => favMovie.id !== movie.id);
    } else {
      const addedMovie: MovieItem = { ...movie, isFavorite: true };
      updatedFavoriteMovies = [addedMovie, ...favoriteMovies!];
    }
    const updatedFavoriteId = updatedFavoriteMovies.map((favMovie) => favMovie.id);
    setFavoriteMovies(updatedFavoriteMovies);
    setFavoriteId(updatedFavoriteId);
    setTotalFavorites(updatedFavoriteId.length);
  };

  const resetFavorites = () => {
    setFavoriteMovies(null);
    setFavoriteId(null);
    setTotalFavorites(0);
  };

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  return {
    favoriteMovies,
    favoriteId,
    totalFavorites,
    setFavoriteMovies,
    setFavoriteId,
    setTotalFavorites,
    fetchFavorites,
    updateFavoriteMovies,
    resetFavorites,
    isLoading,
  };
};

const FavoriteMovies = createContainer(useFavorite);

export default FavoriteMovies;
